import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { Button, IconButton, ButtonGroup } from '@upmc/ims.component-library';
import MaterialIcon from '@material/react-material-icon';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import Fade from '@mui/material/Fade';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { Tooltip } from '../../../../components/UI/MDC/Tooltip/Tooltip';
import { CONTENT_ALIGNMENT } from 'components/UI/MDC/Tooltip/_types';
import { exportApp, importApp } from 'pages/ApplicationMaintenance/_api';

export const ImportExport = ({
	appID = -1,
	selectApp = () => {},
	requirementID = -1,
	user = null,
}) => {
	const dispatch = useDispatch();

	const [sIsDialog, setIsDialog] = useState(false);
	const [sIsLoading, setIsLoading] = useState(false);
	const [sSnackbarMessage, setSnackbarMessage] = useState('');
	const [sRequirementID, setRequirementID] = useState(-1);
	const [sValueImport, setValueImport] = useState('');
	const [sImportedApp, setImportedApp] = useState(null);

	const onExportHandler = async () => {
		setIsLoading(true);
		let objExport = await exportApp(appID);
		navigator.clipboard.writeText(JSON.stringify(objExport));
		setSnackbarMessage('Copied to clipboard!');
		setIsLoading(false);
	};

	const onImportHandler = async () => {
		let isImported = false;
		try {
			setIsLoading(true);

			const app = await importApp(
				JSON.parse(sValueImport),
				sRequirementID,
				user
			);
			setImportedApp(app);
			isImported = app?.id > 0 ? true : false;
		} catch (e) {
			isImported = false;
			if(e.response?.data?.messages?.length > 0){
				raiseErrorBanner(e.response.data.messages[0]?.Value, dispatch);
			}
			else{
				raiseErrorBanner('An error occurred during import.', dispatch);
			}
		} finally {
			setIsDialog(false);
			setIsLoading(false);
			setSnackbarMessage(
				isImported
					? 'Import successful! Reloading...'
					: 'Import unsuccessful.'
			);
		}
	};

	return (
		<>
			<ButtonGroup>
				<Tooltip content='Import' align={CONTENT_ALIGNMENT.BOTTOM}>
					<Button
						sx={{ marginLeft: '10px' }}
						onClick={() => setIsDialog(true)}
					>
						<MaterialIcon icon='file_download' />
					</Button>
				</Tooltip>
				<Tooltip content='Export' align={CONTENT_ALIGNMENT.BOTTOM}>
					<Button
						disabled={
							(sIsLoading && !sIsDialog) ||
							!requirementID ||
							!appID
						}
						onClick={onExportHandler}
					>
						{sIsLoading && !sIsDialog ? (
							<CircularProgress size={24} />
						) : (
							<MaterialIcon icon={'file_upload'} />
						)}
					</Button>
				</Tooltip>
			</ButtonGroup>
			<Dialog
				open={sIsDialog}
				onClose={() => {
					setIsDialog(false);
				}}
			>
				<DialogContent sx={{ paddingBottom: '0' }}>
					<Typography sx={{ maxWidth: '225px' }} variant='body2'>
						The following are excluded from import and should be
						configured manually:
						<ul>
							<li>Email Suffix Scrubbing</li>
							<li>AD Group Mappings</li>
							<li>Work Queue</li>
						</ul>
					</Typography>
					<FormGroup>
						<FormControl margin='normal' required>
							<TextField
								label='Requirement Number'
								type='number'
								onChange={e => setRequirementID(e.target.value)}
								value={sRequirementID}
							/>
						</FormControl>
						<FormControl margin='normal' required>
							<TextField
								sx={{ paddingBottom: '12px' }}
								fullWidth
								multiline
								maxRows={5}
								label='Import Object'
								onChange={e => setValueImport(e.target.value)}
								value={sValueImport}
							/>
						</FormControl>
					</FormGroup>
				</DialogContent>
				<DialogActions>
					<Tooltip content='Import'>
						<IconButton
							color='primary'
							disabled={
								sIsLoading ||
								sRequirementID === -1 ||
								sValueImport === ''
							}
							onClick={onImportHandler}
						>
							{sIsLoading ? (
								<CircularProgress size={24} />
							) : (
								<MaterialIcon icon='file_download' />
							)}
						</IconButton>
					</Tooltip>
				</DialogActions>
			</Dialog>
			<Snackbar
				sx={{ bgcolor: 'secondary' }}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				autoHideDuration={1500}
				open={!!sSnackbarMessage}
				onClose={() => {
					if (sSnackbarMessage?.toLowerCase().includes('reload')) {
						selectApp(sImportedApp);
					}
					setSnackbarMessage('');
					setValueImport('');
					setImportedApp(null);
				}}
				TransitionComponent={Fade}
			>
				<Alert severity='info'>{sSnackbarMessage}</Alert>
			</Snackbar>
		</>
	);
};
